import { useEffect, useMemo } from 'react';
import { Link, useParams, Navigate } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import SEOMeta from '@/components/feature/SEOMeta';
import { articles } from '@/mocks/articles';

export default function ArticleDetailPage() {
  const { id } = useParams<{ id: string }>();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [id]);

  const article = useMemo(() => articles.find((a) => a.id === id), [id]);

  const relatedArticles = useMemo(
    () =>
      article
        ? articles.filter((a) => a.id !== article.id && a.category === article.category).slice(0, 3)
        : [],
    [article],
  );

  if (!article) {
    return <Navigate to="/articles" replace />;
  }

  return (
    <>
      <SEOMeta
        title={`${article.title} - 臻品阁`}
        description={article.summary}
        keywords={article.tags.join(',')}
        canonicalUrl={`https://www.example.com/article/${article.id}`}
        ogType="article"
      />

      {/* Article Schema */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'Article',
            headline: article.title,
            description: article.summary,
            image: article.coverImage,
            author: {
              '@type': 'Person',
              name: article.author,
            },
            datePublished: article.date,
            publisher: {
              '@type': 'Organization',
              name: '臻品阁',
            },
          }),
        }}
      />

      {/* BreadcrumbList Schema */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: [
              { '@type': 'ListItem', position: 1, name: '首页', item: 'https://www.example.com/' },
              { '@type': 'ListItem', position: 2, name: '精选文章', item: 'https://www.example.com/articles' },
              { '@type': 'ListItem', position: 3, name: article.title, item: `https://www.example.com/article/${article.id}` },
            ],
          }),
        }}
      />

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main className="pt-20 md:pt-24">
          {/* Article Header */}
          <article className="w-full">
            <section className="w-full py-10 md:py-14 px-6 md:px-10 bg-background-100/50">
              <div className="max-w-3xl mx-auto">
                <nav className="flex items-center gap-2 text-xs text-foreground-500 mb-4" aria-label="面包屑导航">
                  <Link to="/" className="hover:text-primary-600 transition-colors cursor-pointer">首页</Link>
                  <i className="ri-arrow-right-s-line" />
                  <Link to="/articles" className="hover:text-primary-600 transition-colors cursor-pointer">精选文章</Link>
                  <i className="ri-arrow-right-s-line" />
                  <span className="text-foreground-900 font-medium truncate max-w-[200px]">{article.title}</span>
                </nav>

                <div className="flex items-center gap-3 mb-4">
                  <span className="text-[10px] font-label px-2 py-0.5 rounded-full bg-accent-100 text-accent-700">
                    {article.category}
                  </span>
                  <span className="text-xs text-foreground-400">{article.readTime}</span>
                </div>

                <h1 className="text-xl md:text-2xl lg:text-3xl font-heading font-semibold text-foreground-900 leading-snug">
                  {article.title}
                </h1>

                <div className="flex items-center gap-4 mt-4 text-xs text-foreground-400">
                  <span>{article.author}</span>
                  <span>·</span>
                  <span>{article.date}</span>
                </div>

                <div className="flex flex-wrap gap-1.5 mt-4">
                  {article.tags.map((tag) => (
                    <span key={tag} className="text-[10px] px-2 py-0.5 rounded-full bg-secondary-100 text-secondary-700">
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
            </section>

            {/* Cover Image */}
            <section className="w-full px-6 md:px-10 py-4">
              <div className="max-w-3xl mx-auto">
                <div className="aspect-[16/9] rounded-lg overflow-hidden bg-background-100">
                  <img
                    src={article.coverImage}
                    alt={article.title}
                    title={article.title}
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>
            </section>

            {/* Article Content */}
            <section className="w-full py-8 md:py-10 px-6 md:px-10">
              <div className="max-w-3xl mx-auto">
                <div
                  className="prose prose-sm md:prose-base max-w-none
                    prose-headings:font-heading prose-headings:text-foreground-900 prose-headings:font-semibold
                    prose-h2:text-lg md:prose-h2:text-xl prose-h2:mt-8 prose-h2:mb-4
                    prose-p:text-foreground-700 prose-p:leading-relaxed prose-p:text-sm md:prose-p:text-base
                    prose-strong:text-foreground-900
                    prose-li:text-foreground-700"
                  dangerouslySetInnerHTML={{ __html: article.content }}
                />
              </div>
            </section>

            {/* Related Articles */}
            {relatedArticles.length > 0 && (
              <section className="w-full py-10 md:py-14 px-6 md:px-10 bg-background-100/50">
                <div className="max-w-3xl mx-auto">
                  <h3 className="text-lg font-heading font-semibold text-foreground-900 mb-6">
                    相关文章
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-5">
                    {relatedArticles.map((ra) => (
                      <Link
                        key={ra.id}
                        to={`/article/${ra.id}`}
                        className="group bg-background-50 rounded-lg overflow-hidden border border-background-200/60 hover:border-primary-200/60 transition-all duration-300 cursor-pointer"
                      >
                        <div className="aspect-[16/10] overflow-hidden bg-background-100">
                          <img
                            src={ra.coverImage}
                            alt={ra.title}
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                          />
                        </div>
                        <div className="p-3 md:p-4">
                          <h4 className="text-xs md:text-sm font-label font-medium text-foreground-900 line-clamp-2 group-hover:text-primary-600 transition-colors">
                            {ra.title}
                          </h4>
                          <span className="text-[10px] text-foreground-400 mt-1 block">{ra.date}</span>
                        </div>
                      </Link>
                    ))}
                  </div>
                </div>
              </section>
            )}
          </article>
        </main>

        <Footer />
      </div>
    </>
  );
}